import { useCloudSettingsQuery } from "@repo/baseCloud";
import { TLPGrid, useGridColumns } from "@repo/baseCloud/components";
import { PushErrorNotification, PushSuccessNotification, styles } from "@repo/helpers";
import { Complaint } from "@repo/models";
import { Button, MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { ComplaintsFilter } from "../../models";

import ComplaintsApi, { controller } from "../../api/complaintsApi";
import { QUERY_KEYS } from "../../api/queries";
import { AgentPortal } from "../../apps/agentPortal";
import { AuditEditor } from "../../apps/auditEditor";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import style from "./style.module.scss";

interface Props {
	filters: ComplaintsFilter;
	setFilters: (filters: ComplaintsFilter) => void;
}

export function CustomerIncidentsGrid(props: Props) {
	const { filters, setFilters } = props;
	const { settings } = useCloudSettingsQuery();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [portalComplaint, setPortalComplaint] = useState<Complaint | null>(null);

	const handleDialogOpenChange = (open: boolean) => {
		setDialogOpen(open);
		if (!open) setPortalComplaint(null);
	};

	// Automated Exports (Audit Editor)
	const [auditDialogOpen, setAuditDialogOpen] = useState(false);
	const handleAuditDialogOpenChange = (open: boolean) => setAuditDialogOpen(open);

	const complaintsQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.Complaints, filters],
		queryFn: () =>
			ComplaintsApi.getComplaints({
				...filters,
			}),
	});

	const openPortalModalNew = () => {
		setPortalComplaint(null);
		setDialogOpen(true);
	};

	const openPortalModal = (complaint: Complaint) => {
		setPortalComplaint(complaint);
		setDialogOpen(true);
	};

	const handleDelete = async (complaint: Complaint) => {
		if (!complaint.id) return;
		const ok = window.confirm("Are you sure you want to delete this incident?");
		if (!ok) return;
		try {
			await ComplaintsApi.deleteComplaint(complaint);
			PushSuccessNotification("Incident deleted");
		} catch (err) {
			console.error("Failed to delete incident", err);
			PushErrorNotification("Failed to delete incident. Please try again.");
		} finally {
			await complaintsQuery.refetch();
		}
	};

	const { columns, isLoading: isLoadingColumns } = useGridColumns<Complaint>(
		"CustomerIncident",
		ComplaintsApi.getGridColumns,
		{
			openPortalModal,
			handleDelete,
		},
		// Has showCommissionComplaint and canDelete for the seeded ZPV and Delete columns; the server rejects deletes either way.
		settings?.customerIncidents,
	);

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<Complaint, ComplaintsFilter>
				id="customerIncidentsGrid"
				title="Customer Incidents"
				gridNameForExport="CustomerIncidents"
				pageSizes={[20, 50, 100]}
				keyProperty="complaintId"
				filters={filters}
				setFilters={setFilters}
				query={complaintsQuery}
				columns={columns}
				apiRoute={controller}
				gridApi={ComplaintsApi}
				exportFunc={ComplaintsApi.getComplaints}
				createButtonText="New Customer Incident"
				onCreateClick={openPortalModalNew}
				selected={portalComplaint?.id ? [portalComplaint.id] : []}
			/>
			<div className={style.audit}>
				<Button onClick={() => setAuditDialogOpen(true)}>View Automated Exports</Button>
			</div>
			{dialogOpen ? (
				<MoveableDialog
					open={dialogOpen}
					onOpenChange={handleDialogOpenChange}
					title="Agent Portal"
					initialSize={getInitialDialogSize()}
					resizable
					movable
					className={styles("agent-portal-modal", style.noPadding)}
					contentClassName={style.noPadding}
					footer={
						<Footer
							items={complaintsQuery.data?.items ?? []}
							current={portalComplaint}
							onChange={setPortalComplaint}
						/>
					}
				>
					<AgentPortal
						key={portalComplaint?.id ?? 0}
						repId={portalComplaint?.representativeId ?? null}
						selectedTab="Complaints"
						entityId={portalComplaint?.id}
					/>
				</MoveableDialog>
			) : null}
			{auditDialogOpen ? (
				<MoveableDialog
					open={auditDialogOpen}
					onOpenChange={handleAuditDialogOpenChange}
					title="Automated Exports"
					initialSize={getInitialDialogSize()}
					resizable
					movable
				>
					<AuditEditor code="COMPLAINTS" defaultValues={[]} />
				</MoveableDialog>
			) : null}
		</>
	);
}

function Footer({
	items,
	current,
	onChange,
}: {
	items: Complaint[];
	current: Complaint | null;
	onChange: (c: Complaint) => void;
}) {
	const currentIndex = current ? items.findIndex((c) => c.id === current.id) : -1;
	const hasPrev = currentIndex > 0;
	const hasNext = currentIndex >= 0 && currentIndex < items.length - 1;

	if (!hasPrev && !hasNext) return null;

	const goPrev = () => {
		if (!hasPrev) return;
		onChange(items[currentIndex - 1]);
	};
	const goNext = () => {
		if (!hasNext) return;
		onChange(items[currentIndex + 1]);
	};

	return (
		<div className={style.dialogFooter}>
			<Button disabled={!hasPrev} onClick={goPrev}>
				Prev
			</Button>
			<Button disabled={!hasNext} onClick={goNext}>
				Next
			</Button>
		</div>
	);
}
